import React, { useMemo, useState, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';

import * as LucideIcons from 'lucide-react';
import { AlertCircle, ArrowLeft, ArrowRight, Info } from 'lucide-react';

import {
	create_ticket, get_my_tickets, get_resolved_tickets, get_ticket_exists, reasonsForTarget,
} from '../../actions/ticketActions';

import checkAnimation from '../../static/icons/checkAnimation.json';

// The modal owns its stylesheet so it renders the same whether it's mounted
// from Display (which already loads displayNew.css) or from PayloadNew.
import './displayNew.css';

// A reason's `icon` is a free string holding a lucide icon name. It is resolved
// against the whole library at runtime, so the catalogue can use any icon
// without a code change here. That means importing the full namespace (~150 KB
// gzipped): the deliberate trade for not gatekeeping which icons are allowed.
//
// The stored name may be spelled the way lucide's site lists it ("alert-circle")
// or the way its React snippet does (<AlertCircle />), so both — plus
// snake_case, camelCase and spaces — normalise to the PascalCase export name.
const toPascal = (icon) => String(icon || '')
	.trim()
	// "XCircle" → "X Circle": a run of capitals followed by a capitalised word.
	.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
	// "alertCircle" → "alert Circle": a capital right after a letter or digit.
	.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
	.split(/[\s_-]+/)
	.filter(Boolean)
	.map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
	.join('');

// lucide also exports helpers and a context alongside the icons, so require a
// React component (a forwardRef object) rather than any truthy export.
const isIconComponent = (c) => typeof c === 'object' && c !== null && !!c.$$typeof;

// The create endpoint reports validation failures as a field-keyed dict of
// English strings. Match the ones we know so the user reads an explanation
// instead of the raw payload; anything unmatched still shows verbatim rather
// than being swallowed. Matching is on a substring because the backend embeds
// limits and counts in some of these messages.
const CREATE_ERROR_COPY = [
	{ field: 'target', match: 'already an open ticket', key: 'duplicate' },
	{ field: 'target', match: 'another organization', key: 'foreignOrg' },
	{ field: 'reason', match: 'does not apply', key: 'reasonTarget' },
	{ field: 'proposed', match: 'does not accept a proposal', key: 'noProposal' },
];

const createErrorKey = (field, message) => CREATE_ERROR_COPY.find((e) => (
	e.field === field && String(message).toLowerCase().includes(e.match)
))?.key;

const reasonIcon = (icon) => {
	const candidate = LucideIcons[toPascal(icon)];
	return isIconComponent(candidate) ? candidate : AlertCircle;
};

// Exactly one of `imageId` / `payloadId` should be provided — it decides the
// ticket target, which reasons are offered (via the ticket_types bitmask)
// and which key goes in the create body.
// `organization` is the org of the claimed object, needed so the refetch below
// keeps the same scope the page used; without it /mine falls back to the token's
// own organization and answers empty for a Percepthor user on client data.
const ReportErrorModal = ({ open, onClose, imageId, payloadId, resultId, organization }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const lottieRef = useRef();

	const target = payloadId ? 'payload' : 'image';
	const targetId = payloadId || imageId;

	const { ticket_loading, ticket_create_success, ticket_reasons, ticket_reasons_loading, ticket_settings } = useSelector((s) => s.ticket);
	const ticket_error = useSelector((s) => s.ticket.error);

	// Reasons are fetched by the host page (feeds both the report banner and
	// this modal). Filtered to the ones that apply to this target, then sorted
	// ascending by creation date so newly added reasons stay at the bottom and
	// existing ones don't jump around between reloads.
	const reasons = useMemo(() => {
		const list = reasonsForTarget(ticket_reasons?.reasons, target);
		return [...list].sort((a, b) => (a?.date?.$date ?? 0) - (b?.date?.$date ?? 0));
	}, [ticket_reasons, target]);

	// Description length limits from the effective config (/settings/mine
	// already resolves module → org → defaults). The literals below only cover
	// the window before the request lands, and mirror the backend defaults.
	const { minDescription, maxDescription } = useMemo(() => {
		const cfg = ticket_settings || {};
		return {
			minDescription: Number.isFinite(cfg.min_description) ? cfg.min_description : 10,
			maxDescription: Number.isFinite(cfg.max_description) ? cfg.max_description : 1000,
		};
	}, [ticket_settings]);

	const [step, setStep] = useState(1);
	const [category, setCategory] = useState(null);
	const [description, setDescription] = useState('');
	const [validationError, setValidationError] = useState('');

	const reset = () => {
		setStep(1);
		setCategory(null);
		setDescription('');
		setValidationError('');
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	const selectCategory = (reason) => {
		setCategory(reason);
		setStep(2);
	};

	const handleBack = () => {
		setStep(1);
		setValidationError('');
	};

	const closeRef = useRef();
	closeRef.current = handleClose;

	const handleSubmit = () => {
		const trimmed = description.trim();
		if (!trimmed) {
			setValidationError(t('display.reportError.step2.required'));
			return;
		}
		if (trimmed.length < minDescription) {
			setValidationError(t('display.reportError.step2.minChars', { min: minDescription, defaultValue: 'Mínimo {{min}} caracteres.' }));
			return;
		}
		setValidationError('');

		// Exactly one claimed object goes in the body (contract: image XOR
		// payload XOR response). Backend derives organization/user/context
		// from the token; we only send target + reason + description.
		const ticket_info = {
			reason: category?._id?.$oid,
			description,
			[target]: targetId,
		};

		// Re-read the ticket lookups for this object so the banner switches to
		// the "already reported" notice instead of keeping the outcome of a
		// previous, already-resolved ticket. /exists is what the banner reads,
		// so it has to be refreshed too or the CTA survives the create.
		const refreshTicketState = () => {
			// `create_query_params` stringifies whatever it is handed, so an
			// undefined organization would travel as the literal "undefined".
			const scope = organization ? { organization } : {};
			dispatch(get_ticket_exists(target, targetId));
			dispatch(get_my_tickets({ [target]: targetId, ...scope, multiple: '1,2' }));
			dispatch(get_resolved_tickets({ [target]: targetId, ...scope, multiple: '3,4,5,6,7,8' }));
		};

		dispatch(create_ticket(
			ticket_info,
			{ current: { click: () => setTimeout(() => closeRef.current(), 400) } },
			reset,
			refreshTicketState,
		));
	};

	if (!open) return null;

	const selectedCat = category;

	return (
		<>
			<div className='dn-modal-backdrop' onClick={handleClose} />
			<div className='dn-modal dn-report-modal'>
				{step === 1 && (
					<>
						<div className='dn-report-step-header'>
							<span className='dn-report-step-badge'>
								{t('display.reportError.step', { current: 1, total: 2 })}
							</span>
							<span className='dn-report-step-label'>
								{t('display.reportError.title')}
							</span>
						</div>
						<div className='dn-report-body'>
							<h3 className='dn-report-heading'>
								{t('display.reportError.step1.heading')}
							</h3>
							<p className='dn-report-subtext'>
								{t('display.reportError.step1.subtext')}
							</p>
							<div className='dn-report-categories'>
								{ticket_reasons_loading ? (
									<div className='dn-report-cat-loading'>
										<span className='dn-report-spinner' /> {t('display.reportError.loading', { defaultValue: 'Loading options...' })}
									</div>
								) : reasons.length === 0 ? (
									<div className='dn-report-cat-empty'>
										{t('display.reportError.noReasons', { defaultValue: 'No reasons available.' })}
									</div>
								) : (
									reasons.map((reason) => {
										const ReasonIcon = reasonIcon(reason?.icon);
										return (
										<button
											key={reason?._id?.$oid}
											type='button'
											className='dn-report-cat-card'
											onClick={() => selectCategory(reason)}
										>
											<span className='dn-report-cat-icon'>
												<ReasonIcon size={18} />
											</span>
											<span className='dn-report-cat-content'>
												<span className='dn-report-cat-title'>
													{reason?.label}
												</span>
												<span className='dn-report-cat-desc'>
													{reason?.description}
												</span>
											</span>
											<span className='dn-report-cat-arrow'>
												<ArrowRight size={16} />
											</span>
										</button>
										);
									})
								)}
							</div>
						</div>
						<div className='dn-report-footer'>
							<button type='button' className='dn-report-cancel' onClick={handleClose}>
								{t('display.reportError.cancel')}
							</button>
						</div>
					</>
				)}

				{step === 2 && (
					<>
						<div className='dn-report-step-header'>
							<span className='dn-report-step-badge'>
								{t('display.reportError.step', { current: 2, total: 2 })}
							</span>
							<span className='dn-report-step-label'>
								{selectedCat?.label || ''}
							</span>
						</div>
						<div className='dn-report-body'>
							<h3 className='dn-report-heading'>
								{t('display.reportError.step2.heading')}
							</h3>
							<p className='dn-report-subtext'>
								{t('display.reportError.step2.subtext')}
							</p>

							{ticket_error.isError && ticket_error.type === 'ticket_create' && (() => {
								// The backend returns validation errors as a field-keyed
								// dict; render each line so the user sees the actual
								// reason (e.g. real_date's 30-day window message) instead
								// of a generic Axios status string.
								const fieldEntries = Object.entries(ticket_error.fields || {})
									.filter(([, v]) => typeof v === 'string');
								return (
									<div className='dn-report-alert dn-report-alert--error'>
										{fieldEntries.length > 0
											? fieldEntries.map(([k, v]) => {
												const copyKey = createErrorKey(k, v);
												return (
													<div key={k}>
														{copyKey
															? t(`display.reportError.createErrors.${copyKey}`)
															: v}
													</div>
												);
											})
											: <>{ticket_error.msg}</>
										}
									</div>
								);
							})()}

							<div className='dn-report-field'>
								<label className='dn-report-field-label'>
									{t('display.reportError.step2.detailsLabel')}
								</label>
								<textarea
									className={`dn-report-textarea${validationError ? ' dn-report-textarea--error' : ''}`}
									rows={4}
									value={description}
									onChange={(e) => { setDescription(e.target.value); setValidationError(''); }}
									placeholder={t('display.reportError.step2.placeholder')}
									maxLength={maxDescription}
								/>
								<div className='dn-report-field-meta'>
									{validationError
										? <span className='dn-report-field-error'>{validationError}</span>
										: <span className='dn-report-field-hint'>
											{t('display.reportError.step2.minHint', { min: minDescription, defaultValue: 'Mínimo {{min}} caracteres.' })}
										</span>
									}
									<span className='dn-report-field-counter'>
										{description.length} / {maxDescription}
									</span>
								</div>
							</div>

							<div className='dn-report-notice'>
								<Info size={16} />
								<span>
									{t('display.reportError.step2.notice')}
								</span>
							</div>
						</div>
						<div className='dn-report-footer dn-report-footer--between'>
							<button type='button' className='dn-report-back' onClick={handleBack}>
								<ArrowLeft size={14} />
								{t('display.reportError.back')}
							</button>
							<button
								type='button'
								className={`dn-btn dn-btn--primary${ticket_create_success ? ' dn-btn--success' : ''}`}
								disabled={ticket_loading}
								onClick={handleSubmit}
							>
								{ticket_create_success
									? <Lottie
										loop={false}
										lottieRef={lottieRef}
										animationData={checkAnimation}
										style={{ height: '22px' }}
									/>
									: ticket_loading
										? <Fragment>
											<span className='dn-report-spinner' /> {t('display.reportError.sending')}
										</Fragment>
										: t('display.reportError.submit')
								}
							</button>
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default ReportErrorModal;
